import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import UserAvatar from '~/components/UserAvatar';
import { formatRoleForDisplay } from '~/utils/role-helpers';

export type MentionEntry = {
  id: string;
  displayText: string;
};

export type MentionStaffMember = {
  id: string;
  firstName: string;
  lastName: string;
  degree: string | null;
  role: string;
  profilePicture: string | null;
  officeName: string | null;
};

type MentionDropdownProps = {
  suggestions: MentionStaffMember[];
  activeIndex: number;
  onSelect: (staff: MentionStaffMember) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  zIndex?: number;
  /** Demo Builder: keep inside clicks from closing the list. Default off. */
  stopInsideMouseDown?: boolean;
};

// Gap in pixels between the dropdown edge and the anchor.
const GAP_PX = 4;
// Preferred dropdown height; below this we flip placement to keep the dropdown
// visible inside the viewport. Matches the previous max-h-48 (12rem = 192px).
const PREFERRED_HEIGHT_PX = 192;

type Placement = {
  left: number;
  width: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
};

export function MentionDropdown({
  suggestions,
  activeIndex,
  onSelect,
  onClose,
  anchorRef,
  zIndex = 50,
  stopInsideMouseDown = false,
}: MentionDropdownProps) {
  const activeRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Placement | null>(null);

  // Close when clicking outside the portaled dropdown (same pattern as EmojiPickerPopover).
  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (dropdownRef.current?.contains(target)) return;
      onClose();
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [onClose]);

  // Measure anchor position after DOM mutations and on scroll/resize so the
  // dropdown tracks its anchor even when the page scrolls. The dropdown is
  // anchored above when there is enough room, otherwise flipped below to stay
  // inside the viewport.
  useLayoutEffect(() => {
    function measure() {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceAbove = rect.top - GAP_PX;
      const spaceBelow = window.innerHeight - rect.bottom - GAP_PX;
      const placeAbove = spaceAbove >= PREFERRED_HEIGHT_PX || spaceAbove >= spaceBelow;

      if (placeAbove) {
        setPosition({
          left: rect.left,
          width: rect.width,
          bottom: window.innerHeight - rect.top + GAP_PX,
          maxHeight: Math.min(PREFERRED_HEIGHT_PX, Math.max(0, spaceAbove)),
        });
      } else {
        setPosition({
          left: rect.left,
          width: rect.width,
          top: rect.bottom + GAP_PX,
          maxHeight: Math.min(PREFERRED_HEIGHT_PX, Math.max(0, spaceBelow)),
        });
      }
    }

    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [anchorRef, suggestions]);

  // Scroll the active item into view whenever the keyboard selection changes.
  useEffect(() => {
    if (activeRef.current && typeof activeRef.current.scrollIntoView === 'function') {
      activeRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  // Guard against SSR and the initial render before measurement.
  if (typeof document === 'undefined' || !position) return null;

  const grouped = new Map<string, MentionStaffMember[]>();
  for (const s of suggestions) {
    const key = s.officeName || 'Other';
    const list = grouped.get(key) || [];
    list.push(s);
    grouped.set(key, list);
  }

  let flatIndex = 0;

  return createPortal(
    <div
      ref={dropdownRef}
      data-mention-dropdown
      style={{
        position: 'fixed',
        left: position.left,
        ...(position.top !== undefined ? { top: position.top } : {}),
        ...(position.bottom !== undefined ? { bottom: position.bottom } : {}),
        width: position.width,
        maxHeight: position.maxHeight,
        zIndex,
      }}
      className="overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
      onMouseDown={stopInsideMouseDown ? event => event.stopPropagation() : undefined}
    >
      {Array.from(grouped.entries()).map(([officeName, members]) => (
        <div key={officeName}>
          <div className="sticky top-0 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-500 border-b border-gray-100">
            {officeName}
          </div>
          {members.map(member => {
            const idx = flatIndex++;
            const isActive = idx === activeIndex;
            const displayName = member.degree
              ? `${member.firstName} ${member.lastName}, ${member.degree}`
              : `${member.firstName} ${member.lastName}`;

            return (
              <button
                key={member.id}
                ref={isActive ? activeRef : undefined}
                type="button"
                onMouseDown={e => {
                  e.preventDefault();
                  onSelect(member);
                }}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${
                  isActive ? 'bg-purple-50' : 'hover:bg-gray-50'
                }`}
              >
                <UserAvatar
                  name={`${member.firstName} ${member.lastName}`}
                  size={24}
                  className="h-6 w-6"
                  profilePicture={member.profilePicture}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-gray-900 truncate">{displayName}</div>
                  <div className="text-xs text-gray-500">{formatRoleForDisplay(member.role)}</div>
                </div>
              </button>
            );
          })}
        </div>
      ))}
    </div>,
    document.body
  );
}
